// Pure record-processing helpers for the v2 data layer.
//
// These encapsulate the websocket payload handling that was previously
// inlined in Dashboard.tsx: parsing the `type:app/service` field, assigning
// fallback ids/timestamps, deduplicating chunk appends, and applying
// DynamoDB-stream data_change events.

import { DeploymentRecord, WebSocketMessage } from '../../types/DeploymentRecord';
import { parseService, extractJobType } from '../../utils/serviceParser';
import { isNewerSequence } from '../../utils/sequence';

/** Parse a raw websocket payload item into a normalized DeploymentRecord. */
export const processRecord = (item: any, fallbackSuffix?: string | number): DeploymentRecord => {
  const parsed = parseService(item.service || '');
  return {
    ...item,
    id: item.id || `${item.env}-${item.service}-${fallbackSuffix ?? Date.now()}`,
    timestamp: item.timestamp || new Date().toISOString(),
    app: parsed.app,
    // parseService returns '' when `service` has no slash to split on — true
    // for every static job in the fleet (its release_package is
    // `{asset_type}:{asset_name}`, with no job segment, unlike every other
    // type). job_id is a real, always-present field and is what
    // serviceRecordMatches/serviceHref actually key off, so fall back to it
    // rather than leaving serviceName empty (which used to surface the raw,
    // unclickable `service` string instead).
    serviceName: parsed.serviceName || item.job_id || '',
    jobType: item.jobType ?? extractJobType(item),
    // Snake_case fallbacks mirroring the backend's mapRecordFields, in case
    // a deployed lambda version sends unmapped DynamoDB field names.
    buildUser: item.buildUser ?? item.build_user,
    envName: item.envName ?? item.env_name,
    version: item.version ?? item.release_version,
  };
};

export const processRecords = (items: any[]): DeploymentRecord[] =>
  items.map((item, index) => processRecord(item, `${Date.now()}-${index}`));

/** Append incoming records, skipping ids already present (chunk dedup). */
export const appendNewRecords = (
  prev: DeploymentRecord[],
  incoming: DeploymentRecord[]
): DeploymentRecord[] => {
  const existing = new Set(prev.map(r => r.id));
  const fresh = incoming.filter(r => !existing.has(r.id));
  return fresh.length > 0 ? [...prev, ...fresh] : prev;
};

/**
 * Insert-or-update by id; new records go to the front (most recent first).
 * When the record already exists and the incoming stream sequence is not newer
 * than the stored one, the update is dropped (out-of-order delivery) and the
 * exact same array reference is returned so React state updates no-op.
 */
export const upsertRecord = (
  prev: DeploymentRecord[],
  record: DeploymentRecord
): DeploymentRecord[] => {
  const existing = prev.find(r => r.id === record.id);
  if (existing) {
    if (!isNewerSequence(record.sequence, existing.sequence)) return prev;
    return prev.map(r => (r.id === record.id ? { ...r, ...record } : r));
  }
  return [record, ...prev];
};

/**
 * The processed, sequence-stamped record carried by an INSERT/MODIFY
 * data_change, or null for REMOVE/malformed. A single processRecord call shared
 * by the live-list apply and the flash highlight.
 */
export const dataChangeRecord = (message: WebSocketMessage): DeploymentRecord | null => {
  if (message.changeType !== 'INSERT' && message.changeType !== 'MODIFY') return null;
  if (!message.newData) return null;
  const rec = processRecord(message.newData);
  // Only stamp when present; never write `undefined`, which would clobber a
  // stored sequence via upsertRecord's `{...r, ...record}` merge.
  if (message.sequence) rec.sequence = message.sequence;
  return rec;
};

/** Apply a DynamoDB-stream data_change message to the live record list. */
export const applyDataChange = (
  prev: DeploymentRecord[],
  message: WebSocketMessage
): DeploymentRecord[] => {
  const rec = dataChangeRecord(message);
  if (rec) return upsertRecord(prev, rec);
  if (message.changeType === 'REMOVE' && message.oldData) {
    return prev.filter(r => r.id !== message.oldData.id);
  }
  return prev;
};

/**
 * Whether a sibling plan link (resolved from another live record sharing
 * app+jobId+type+env) may be surfaced on `record`. A plan STRICTLY OLDER than
 * the displayed record is a previous run's plan — never surface it on a newer
 * run (a just-started run must not show the prior run's plan button). A plan
 * at/after the record's timestamp is the legit fallback: an older
 * currently-deployed row showing a newer plan-only run's plan. Unparseable or
 * missing timestamps ⇒ allow (legacy behavior).
 */
export const planFallbackApplies = (planTs: number | undefined, record: DeploymentRecord): boolean => {
  if (planTs === undefined || Number.isNaN(planTs)) return true;
  const rts = new Date(record.timestamp).getTime();
  return Number.isNaN(rts) || planTs >= rts;
};

/** Key used for historical request/response correlation: `app:type:app/svc`. */
export const historicalKey = (appName: string, service: string): string =>
  `${appName}:${service}`;

/**
 * URL to a service's detail page, disambiguated by job type when known. An app
 * can have two jobs with the same job_id but different types (e.g. a `lambda`
 * and a `terraform` job both named `lambda`); the `?type=` param keeps them on
 * distinct pages. Omitting type preserves the legacy (type-agnostic) URL.
 */
export const serviceHref = (app: string, jobId: string, type?: string): string => {
  const base = `/services/${encodeURIComponent(app)}/${encodeURIComponent(jobId)}`;
  return type ? `${base}?type=${encodeURIComponent(type)}` : base;
};

/**
 * Whether a record belongs to the (app, jobId[, type]) a service page targets.
 * When `type` is given, it must match — so two same-named jobs of different
 * types don't collapse onto one page.
 */
export const serviceRecordMatches = (
  r: DeploymentRecord,
  app: string,
  jobId: string,
  type?: string
): boolean =>
  r.app === app &&
  (r.serviceName === jobId || r.job_id === jobId) &&
  (!type || (r.type || '').toLowerCase() === type.toLowerCase());

/**
 * Type-qualified identity for a service, used as the stored/matched value in
 * every shared service-filter UI (the compare selector, the Job ID filters
 * on Services/Environment pages, the Activity filter bar and its saved
 * filters). Mirrors groupServices' own grouping key exactly, so two jobs
 * that share a name but differ in type (an `ecs` deploy and a `terraform`
 * plan both named "ecs") are selectable independently instead of a bare
 * name silently matching both.
 */
export const serviceFilterKey = (type: string | undefined, serviceName: string): string =>
  `${(type || '').toLowerCase()}:${serviceName}`;

/**
 * Recovers the display label from a serviceFilterKey — strips the type
 * prefix back off so the user sees "ecs", not "ecs:ecs".
 */
export const serviceFilterLabel = (key: string): string => {
  const idx = key.indexOf(':');
  return idx === -1 ? key : key.slice(idx + 1);
};

/**
 * Display label for a serviceFilterKey as `jobId:type` — used where two
 * same-named jobs of different types (an `ecs` deploy vs. a `terraform` plan
 * both named "ecs") would otherwise render identically under the bare
 * serviceFilterLabel, e.g. AppPage's compare-services picker.
 */
export const serviceCompareLabel = (key: string): string => {
  const idx = key.indexOf(':');
  if (idx === -1) return key;
  const type = key.slice(0, idx);
  const jobId = key.slice(idx + 1);
  return type ? `${jobId}:${type}` : jobId;
};

/**
 * Stable cache key for a record's artifact lookup. Terraform artifacts are
 * version-agnostic; every other type is keyed by version so each env's deployed
 * version resolves to its own commit. Mirrors the params sent in getArtifactData.
 */
export const artifactKey = (r: DeploymentRecord): string => {
  const app = r.app || r.app_name || '';
  const job = r.job_id || r.service?.split('/').pop() || '';
  const type = r.type || 'lambda';
  return type.toLowerCase() === 'terraform'
    ? `${app}:${job}:terraform`
    : `${app}:${job}:${type}:${r.version ?? ''}`;
};

/**
 * Stable React key for a deployment row. `id` is optional on DeploymentRecord,
 * so fall back to a composite of identifying fields (plus the list index as a
 * last resort) to avoid undefined/duplicate keys breaking reconciliation.
 */
export const deploymentKey = (r: DeploymentRecord, index?: number): string =>
  r.id ??
  `${r.envName || r.env || ''}:${r.service || ''}:${r.version || ''}:${r.timestamp || ''}:${index ?? ''}`;

/**
 * Fold stray-named jobs onto their owning app's card.
 *
 * `app` (from parseService, set by processRecord) is normally the right
 * grouping key even though it isn't `app_name`: a single Terraform app_name
 * can be a deliberate umbrella over several unrelated, independently-shipped
 * products (e.g. several static sites sharing one app_name) that must stay
 * visually separate — none of those jobs' parsed `app` equals the app_name,
 * and that's intentional, not a bug.
 *
 * It breaks the other direction: an app_name whose job types mostly embed it
 * (so their parsed `app` already equals app_name) can still have one job
 * whose parsed identity diverges — e.g. a `static` job, whose release
 * package is `{asset_type}:{asset_name}` with no app segment at all, unlike
 * every other type. That job never joins its siblings' card.
 *
 * This distinguishes the two cases using only data already on the records:
 * an app_name has a "namesake" when some record with that app_name already
 * has `app === app_name` — proof the app_name names one real product, not a
 * pure umbrella. Only app_names with a namesake get strays folded in; a
 * namesake-less (umbrella) app_name's records are returned untouched, so
 * apps like that keep today's fully separate cards with zero behavior
 * change.
 */
export const foldStrayAppsIntoNamesakes = (records: DeploymentRecord[]): DeploymentRecord[] => {
  const namesakeAppNames = new Set<string>();
  records.forEach(r => {
    if (r.app_name && r.app === r.app_name) namesakeAppNames.add(r.app_name);
  });
  if (namesakeAppNames.size === 0) return records;
  return records.map(r =>
    r.app_name && r.app !== r.app_name && namesakeAppNames.has(r.app_name) ? { ...r, app: r.app_name } : r
  );
};
